/**
 * **All**: the activity counts behind the boundary hover cards — client change, 2026-09-23.
 *
 * > *"when a user clicks on 'All' on the rail, if there was any data there it should
 * > disappear and we should have a single symbol colour for the operational hierarchy. Then
 * > we should not have to click on a box — once a user starts hovering on the map, the
 * > pop-ups appear."*
 *
 * The 19 September iteration drew a crystal square per country and asked the reader to find
 * it and click it. This is the same card with the box taken away: the boundary the reader is
 * already pointing at *is* the target, at every level of the hierarchy, and pointing is the
 * whole gesture.
 *
 * ## What this component is
 *
 * It renders nothing and it owns no Leaflet object. It is **five queries and a store write**:
 * one query per §F5.8 row that is currently drawn, each for the grouping that row is about
 * (`GROUP_BY_ROW`), landing in `useActivityHoverStore`. `HierarchyLayers` reads that store at
 * `mouseover` and builds the card — see `activityTips.ts` for why the data travels that way
 * round rather than down a prop.
 *
 * A row that is not drawn is not fetched. Communes below zoom 7 are ~1 050 units of counts
 * nobody can hover, and the gate that keeps the geometry off the map keeps the counts off the
 * wire too — `rowActive` is the same predicate both sides ask.
 *
 * ## The counts follow the explorer, not the map
 *
 * `country`, `selectedUnit` and the chosen year are all in the query key, so narrowing the
 * scope narrows the numbers. They do **not** follow the viewport: panning is not a filter
 * anywhere else in this app and a count that changed on a drag would be unquotable.
 */
import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { fetchActivitiesByUnit, queryKeys, type ActivitiesByUnit } from '../../lib/api';
import { asLanguage, DEFAULT_LANGUAGE } from '../../lib/i18n';
import { selectYear, useAppStore } from '../../lib/state';
import { ALL_THEME, themeOf } from '../sidebar/themes';
import { GROUP_BY_ROW, useActivityHoverStore } from './activityTips';
import { HIERARCHY_ROWS, rowActive, useHierarchyStore, type HierarchyRow } from './hierarchy';
import { useMap, useMapViewState } from './mapContext';

/** `units` is the payload's own field; `countries` is the alias the server kept for `country`. */
function unitsOf(data: ActivitiesByUnit) {
  return data.units ?? data.countries ?? [];
}

function ActivityHoverRow({ row, zoom, active }: { row: HierarchyRow; zoom: number; active: boolean }) {
  const { i18n } = useTranslation();
  const lang = asLanguage(i18n.resolvedLanguage ?? i18n.language) ?? DEFAULT_LANGUAGE;
  const country = useAppStore((s) => s.country);
  const scope = useAppStore((s) => s.scope);
  const unit = useAppStore((s) => s.selectedUnit);
  // The year narrows the counts only once the reader has *chosen* one (`yearChosen`): the
  // boot default is the indicator's year, not a filter anybody set, and applying it here hid
  // most of the registry — the dev stack's 2025 default showed one Nigerian activity out of
  // 110. `yearPinned` is not the signal: the boot default pins too.
  const chosen = useAppStore((s) => s.yearChosen);
  const chosenYear = useAppStore(selectYear);
  const year = chosen ? chosenYear : null;
  const visible = useHierarchyStore((s) => s.visible);
  const setRowUnits = useActivityHoverStore((s) => s.setRowUnits);
  const clearRow = useActivityHoverStore((s) => s.clearRow);

  // The same predicate `HierarchyLayers` draws by, so a card can never be promised for a
  // boundary that is not on the map and a drawn boundary is never left without its counts.
  const enabled = active && rowActive(row, visible, zoom, country, scope);
  const { data } = useQuery({
    queryKey: queryKeys.activitiesByUnit(GROUP_BY_ROW[row.key], country, unit, year, lang),
    queryFn: fetchActivitiesByUnit,
    enabled,
  });

  useEffect(() => {
    if (!enabled || !data) return;
    setRowUnits(row.key, unitsOf(data));
    // Leaving All, unticking the row, changing the scope: the row's answer goes with it,
    // and the card falls back to the name rather than quoting the previous country's counts.
    return () => clearRow(row.key);
  }, [enabled, data, row.key, setRowUnits, clearRow]);

  return null;
}

export function ActivityHover() {
  const map = useMap();
  const { zoom } = useMapViewState(map);
  // The rail's current theme. `domFilter` is the store field a theme selection writes, and
  // `themeOf` is the one place that knows the empty string means All (`sidebar/themes.ts`).
  const active = useAppStore((s) => themeOf(s.domFilter)) === ALL_THEME;
  return (
    <>
      {HIERARCHY_ROWS.map((row) => (
        <ActivityHoverRow key={row.key} row={row} zoom={zoom} active={active} />
      ))}
    </>
  );
}

export default ActivityHover;
